/**
 * IndexedDB User Repository Implementation
 *
 * Implements IUserRepository using Dexie.js for IndexedDB storage.
 */

import { db } from '../../db';
import type { IUserRepository } from '../interfaces/IUserRepository';
import type { User, UserRole, UserStatus, RequesterInfo } from '../../../types/ticket';
import { UserStatus as UserStatusEnum } from '../../../types/ticket';
import type { CreateUserDTO, UpdateUserDTO } from '../../../types/dto';

export class IndexedDBUserRepository implements IUserRepository {
  async getAll(): Promise<User[]> {
    return db.users.toArray();
  }

  async getById(id: string): Promise<User | null> {
    const user = await db.users.get(id);
    return user ?? null;
  }

  async getByEmail(email: string): Promise<User | null> {
    const user = await db.users.where('email').equals(email).first();
    return user ?? null;
  }

  async create(data: CreateUserDTO): Promise<User> {
    const now = new Date();
    const user: User = {
      id: crypto.randomUUID(),
      email: data.email,
      name: data.name,
      role: data.role,
      department: data.department,
      status: data.status ?? UserStatusEnum.ACTIVE,
      location: data.location,
      createdAt: now,
      updatedAt: now,
      lastLogin: undefined,
    };

    await db.users.add(user);
    return user;
  }

  async update(id: string, data: UpdateUserDTO): Promise<User | null> {
    const existing = await this.getById(id);
    if (!existing) {
      return null;
    }

    const updatedUser: User = {
      ...existing,
      ...data,
      updatedAt: new Date(),
    };

    await db.users.put(updatedUser);
    return updatedUser;
  }

  async delete(id: string): Promise<boolean> {
    const existing = await this.getById(id);
    if (!existing) {
      return false;
    }
    await db.users.delete(id);
    return true;
  }

  async getByRole(role: UserRole): Promise<User[]> {
    return db.users.where('role').equals(role).toArray();
  }

  async getByStatus(status: UserStatus): Promise<User[]> {
    return db.users.where('status').equals(status).toArray();
  }

  async search(query: string): Promise<User[]> {
    if (!query.trim()) {
      return this.getAll();
    }

    const lowerQuery = query.toLowerCase();
    const all = await this.getAll();

    return all.filter(
      (user) =>
        user.name.toLowerCase().includes(lowerQuery) ||
        user.email.toLowerCase().includes(lowerQuery)
    );
  }

  async updateStatus(id: string, status: UserStatus): Promise<User | null> {
    return this.update(id, { status });
  }

  async updateLastLogin(id: string): Promise<User | null> {
    return this.update(id, { lastLogin: new Date() });
  }

  async getFiltered(filters: {
    roles?: UserRole[];
    statuses?: UserStatus[];
    searchQuery?: string;
  }): Promise<User[]> {
    let users = await this.getAll();

    if (filters.roles && filters.roles.length > 0) {
      users = users.filter((u) => filters.roles!.includes(u.role));
    }

    if (filters.statuses && filters.statuses.length > 0) {
      users = users.filter((u) => filters.statuses!.includes(u.status));
    }

    if (filters.searchQuery && filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase();
      users = users.filter(
        (u) =>
          u.name.toLowerCase().includes(query) || u.email.toLowerCase().includes(query)
      );
    }

    return users;
  }

  async searchUsers(query: string, limit: number = 10): Promise<RequesterInfo[]> {
    if (!query.trim()) {
      return [];
    }

    const lowerQuery = query.toLowerCase();
    const allUsers = await this.getAll();

    // Filter active users matching the query
    const matchingUsers = allUsers.filter(
      (user) =>
        user.status === UserStatusEnum.ACTIVE &&
        (user.name.toLowerCase().includes(lowerQuery) ||
          user.email.toLowerCase().includes(lowerQuery))
    );

    // Sort by relevance: exact name matches first, then name starts with, then email matches
    matchingUsers.sort((a, b) => {
      const aNameLower = a.name.toLowerCase();
      const bNameLower = b.name.toLowerCase();
      const aEmailLower = a.email.toLowerCase();
      const bEmailLower = b.email.toLowerCase();

      // Exact name match
      if (aNameLower === lowerQuery && bNameLower !== lowerQuery) return -1;
      if (bNameLower === lowerQuery && aNameLower !== lowerQuery) return 1;

      // Name starts with query
      const aNameStarts = aNameLower.startsWith(lowerQuery);
      const bNameStarts = bNameLower.startsWith(lowerQuery);
      if (aNameStarts && !bNameStarts) return -1;
      if (bNameStarts && !aNameStarts) return 1;

      // Email starts with query
      const aEmailStarts = aEmailLower.startsWith(lowerQuery);
      const bEmailStarts = bEmailLower.startsWith(lowerQuery);
      if (aEmailStarts && !bEmailStarts) return -1;
      if (bEmailStarts && !aEmailStarts) return 1;

      // Alphabetical by name
      return aNameLower.localeCompare(bNameLower);
    });

    // Limit results and map to RequesterInfo
    return matchingUsers.slice(0, limit).map((user) => ({
      id: user.id,
      name: user.name,
      email: user.email,
      department: user.department,
      location: user.location,
      employeeId: user.employeeId,
      cdsid: user.cdsid,
      clientLOB: user.clientLOB,
      lobProgram: user.lobProgram,
      lobSite: user.lobSite,
    }));
  }
}
